import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Package, CheckCircle2, Truck, Home, Loader2 } from 'lucide-react';
export function OrderStatus() {
  const [lookupValue, setLookupValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!lookupValue.trim()) return;
    setIsSearching(true);
    // Simulate network request
    setTimeout(() => {
      setIsSearching(false);
      setHasSearched(true);
    }, 800);
  };
  const stages = [{
    id: 'received',
    label: 'Order Received',
    description: 'We have received your order request.',
    icon: Package,
    completed: true,
    date: 'Today, 8:45 AM'
  }, {
    id: 'confirmed',
    label: 'Confirmed',
    description: 'Your order has been reviewed and confirmed.',
    icon: CheckCircle2,
    completed: true,
    date: 'Today, 9:15 AM'
  }, {
    id: 'out',
    label: 'Out for Delivery',
    description: 'Your materials are on the way.',
    icon: Truck,
    completed: false,
    date: 'Pending'
  }, {
    id: 'delivered',
    label: 'Delivered',
    description: 'Your order has been delivered.',
    icon: Home,
    completed: false,
    date: 'Pending'
  }];
  return <main className="min-h-[calc(100vh-113px)] bg-[#fdfbf7] dark:bg-[#222222] py-12 px-4 sm:px-6 lg:px-8" data-id="element-1836">
      <div className="max-w-3xl mx-auto" data-id="element-1837">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-10" data-id="element-1838">
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-stone-900 dark:text-white mb-4" data-id="element-1839">
            Track Your <span className="text-brand-orange" data-id="element-1840">Order</span>
          </h1>
          <p className="text-lg text-stone-600 dark:text-stone-300" data-id="element-1841">
            Enter your phone number or email to check your order status.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white dark:bg-[#2a2a2a] rounded-2xl shadow-xl border border-stone-200 dark:border-white/10 p-6 sm:p-8 mb-8" data-id="element-1842">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4" data-id="element-1843">
            <div className="flex-1 relative" data-id="element-1844">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1845" />
              <input type="text" value={lookupValue} onChange={e => setLookupValue(e.target.value)} placeholder="Phone number or email..." className="w-full pl-12 pr-4 py-4 bg-stone-50 dark:bg-black/20 border border-stone-200 dark:border-white/10 rounded-xl text-stone-900 dark:text-white placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-transparent transition-all" required data-id="element-1846" />
            </div>
            <button type="submit" disabled={isSearching || !lookupValue.trim()} className="px-8 py-4 bg-brand-button hover:bg-brand-buttonHover disabled:opacity-50 text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2 shrink-0" data-id="element-1847">
              {isSearching ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-1848" /> : 'Track Order'}
            </button>
          </form>
        </motion.div>

        {hasSearched && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} className="bg-white dark:bg-[#2a2a2a] rounded-2xl shadow-xl border border-stone-200 dark:border-white/10 p-6 sm:p-10" data-id="element-1849">
            <div className="mb-8 pb-6 border-b border-stone-100 dark:border-white/10" data-id="element-1850">
              <h2 className="text-2xl font-bold text-stone-900 dark:text-white mb-2" data-id="element-1851">
                Order Status
              </h2>
              <p className="text-stone-500 dark:text-stone-400" data-id="element-1852">
                Order #253-{Math.floor(Math.random() * 10000)}
              </p>
            </div>

            <div className="relative" data-id="element-1853">
              {/* Vertical Line */}
              <div className="absolute left-[23px] top-8 bottom-8 w-0.5 bg-stone-200 dark:bg-white/10" data-id="element-1854" />

              <div className="space-y-8" data-id="element-1855">
                {stages.map((stage, index) => {
              const Icon = stage.icon;
              return <div key={stage.id} className="relative flex items-start gap-6" data-id="element-1856">
                      <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-4 border-white dark:border-[#2a2a2a] shrink-0 transition-colors ${stage.completed ? 'bg-[#1DB954] text-white' : 'bg-stone-200 dark:bg-stone-700 text-stone-400 dark:text-stone-500'}`} data-id="element-1857">
                        <Icon className="w-5 h-5" data-id="element-1858" />
                      </div>
                      <div className="pt-2" data-id="element-1859">
                        <h3 className={`text-lg font-bold ${stage.completed ? 'text-stone-900 dark:text-white' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-1860">
                          {stage.label}
                        </h3>
                        <p className="text-stone-500 dark:text-stone-400 mt-1" data-id="element-1861">
                          {stage.description}
                        </p>
                        <p className="text-sm font-medium text-stone-400 dark:text-stone-500 mt-2" data-id="element-1862">
                          {stage.date}
                        </p>
                      </div>
                    </div>;
            })}
              </div>
            </div>
          </motion.div>}
      </div>
    </main>;
}